import React, { FC } from 'react'
import { Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Form, TextField } from '@carsys/form'
import * as Yup from 'yup'
import { BaseStepLayout } from '../BaseStepLayout'
import { StepNavigation } from '../StepNavigation'
import { defaultStepFormProps } from '../types.d'
import { WithRunInBackgroundHelper } from '../use-should-run-in-background'
import { useSignupCredentialsValidatorBuilder } from 'app/auth/components/hooks/use-signup-credentials-validator-builder'

interface FormValues {
  EmailAddress: string
  ConfirmEmailAddress: string
  Name: string
  FirstName: string
  NamePrefix: string
}

export const SignUpForm: FC<
  defaultStepFormProps<FormValues> & {
    onSubmit: (v: FormValues) => void
    basePath: string
  }
> = ({
  onSubmit,
  basePath,
  prevStep,
  isFirstStep,
  isPersisting,
  onSubmitSuccess,
  hideSkip,
  shouldRunInBackground,
  initialData,
  duplicationCheck
}) => {
  const { t } = useTranslation()
  const validateDuplicateEmail = useSignupCredentialsValidatorBuilder(duplicationCheck, basePath)
  const requiredMessage = t('global.field_is_required')
  const validationSchema = Yup.object().shape({
    EmailAddress: Yup.string().required(requiredMessage),
    ConfirmEmailAddress: Yup.string()
      .required(requiredMessage)
      .equals([Yup.ref('EmailAddress')], t('Account.ConfirmEmailAddressError')),
    Name: Yup.string().required(requiredMessage),
    FirstName: Yup.string().required(requiredMessage),
    NamePrefix: Yup.string()
  })

  const initialValues: FormValues = {
    EmailAddress: '',
    Name: '',
    FirstName: '',
    NamePrefix: '',
    ...initialData,
    ConfirmEmailAddress: initialData.EmailAddress || ''
  }

  const submitSignUp = React.useCallback(async (v: FormValues) => {
    await validateDuplicateEmail.mutateAsync({ loginEmail: v.EmailAddress })
    onSubmit(v)
    return v
  }, [])

  return (
    <BaseStepLayout title={t('Auth.OnboardingTitle')} subTitles={[t('Auth.OnboardingSignUpMessage')]}>
      <Form
        enableReinitialize
        NavigationConfirmation={null}
        initialValues={initialValues}
        onSubmit={submitSignUp}
        onSubmitSuccess={onSubmitSuccess}
        validationSchema={validationSchema}
      >
        {formikProps => (
          <WithRunInBackgroundHelper
            enabled={shouldRunInBackground}
            isSubmitting={formikProps.isSubmitting}
            onSubmit={formikProps.handleSubmit}
          >
            <Grid container spacing={2} sx={{ display: 'inline-block', mb: 1 }}>
              <Grid item xs={12} lg={6}>
                <Grid container spacing={2} justifyContent='center' alignItems='center'>
                  <Grid item xs={12}>
                    <TextField
                      onPaste={e => e.preventDefault()}
                      required
                      name='EmailAddress'
                      label={t('Auth.OnboardingSignUpEmail')}
                      autoFocus
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      onPaste={e => e.preventDefault()}
                      required
                      name='ConfirmEmailAddress'
                      label={t('Account.ConfirmEmailAddress')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField required name='FirstName' label={t('Account.FirstName')} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField name='NamePrefix' label={t('Account.NamePrefix')} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField required name='Name' label={t('Account.Name')} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <StepNavigation
              prevStep={prevStep}
              isLoading={formikProps.isSubmitting || isPersisting}
              hideBack={isFirstStep}
              hideSkip={hideSkip}
            />
          </WithRunInBackgroundHelper>
        )}
      </Form>
    </BaseStepLayout>
  )
}
